<template>
  <div class="upsell-carousel-container">
    <ul
      ref="container"
      @scroll="scrollMethod"
    >
      <li
        v-for="(slide, index) in slides"
        :key="index"
        ref="slides"
        :class="{ active: index == active }"
      >
        <img :src="getAttachmentUrl(slide)">
        <div>{{ slide.line1 }}</div>
        <div>{{ slide.line2 }}</div>
      </li>
    </ul>
    <carousel-navigation
      :active-index="active"
      :length="slides.length"
      @navigate="moveToSlide"
    />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import CarouselNavigation from '@/components/carousels/carousel-navigation';

export default {
  components: { CarouselNavigation },
  props: {
    slides: {
      type: Array,
      default: [],
    },
  },
  data() {
    return {
      active: 0,
      time: 0,
      timer: null,
    };
  },
  mounted() {
    this.$watch('active', (index) => {
      const slide = this.slides[index];
      if (slide) {
        this.$analytics.events.viewedBenefit(slide.line1);
      }
    }, { immediate: true });
  },
  methods: {
    getAttachmentUrl(slide) {
      return this.$store.getters['attachments/get'][slide.attachment]?.url;
    },
    moveToSlide(index) {
      // distance is 68% plus 5% margin twice
      let distance = this.$refs.container.offsetWidth * .78 * index;
      this.$refs.container.scroll({ top: 0, left: distance, behavior: 'smooth' });
    },
    scroll(event) {
      // reset the timer on every scroll
      this.time = 0;

      const active = this.$refs.slides[this.active];
      const container = event.target;

      // refs will be null when timer triggers native scroll/scrollTo method and then modal is closed
      // there's not really a way to stop those events once the element starts getting scrolled into the center
      if (!active || !container) return;

      // 5% margin in CSS x width of carousel container
      const margin = 0.05 * container.offsetWidth;
      // scroll position of the center of the container relative to current scroll
      const center = container.scrollLeft + (0.5 * container.offsetWidth);

      // if currently active slide's left edge is in view and is past the center combined with margin we are going left
      const moveLeft = active.offsetLeft > container.scrollLeft && active.offsetLeft > (center + margin);
      // if currently active slide's right edge is in view and is past the center combined with margin we are going right
      const moveRight = (active.offsetLeft + active.offsetWidth) < (container.scrollLeft + container.offsetWidth) && (active.offsetLeft + active.offsetWidth + margin) < center;

      if (moveLeft || moveRight) {
        if (moveRight) {
          this.active = this.active + 1;
        } else {
          this.active = this.active - 1;
        }
      }
    },
    scrollMethod(e) {
      debounce(this.scroll, 200)(e);
    },
    setup() {
      this.active = 0;
      this.time = 0;

      const context = this;
      this.timer = setInterval(function() {
        // if for some reason the refs aren't there, clear the timer
        if (!context.$refs.container) {
          clearInterval(context.timer);
        } else if (context.time > 4) {
          const lastSlide = context.active + 1 > context.slides.length - 1;
          lastSlide ? context.moveToSlide(0) : context.moveToSlide(context.active + 1);
        } else {
          context.time = context.time + 1;
        }
      }, 1000);
    },
  },
};
</script>

<style lang='scss' scoped>
  .upsell-carousel-container {
    overflow: hidden;

    ul {
      @include hide-scrollbar();
      font-size: 0;
      height: rem-calc(380);
      line-height: rem-calc(380);
      list-style: none;
      margin: 0;
      overflow-x: auto;
      overflow-y: hidden;
      padding: 0;
      scroll-behavior: smooth;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      white-space: nowrap;

      @mixin slide {
        border-radius: 8px;
        box-sizing: border-box;
        display: inline-block;
        height: 80%;
        line-height: initial;
        overflow: hidden;
        padding: rem-calc(16) 6%; // 6% of slide shows when inactive
        position: relative;
        scroll-snap-align: center;
        transition: transform .15s linear;
        vertical-align: middle;
        white-space: normal;

        // disable center alignment for chrome since 81+ has scroll snap bug
        @include browser(chrome) {
          scroll-snap-align: initial;
        }
      }

      &::before, &::after {
        @include slide;
        background: transparent;
        box-shadow: none;
        content: '';
        font-size: 0;
        margin: 0;
        padding: 0;
        width: 11%;
      }

      li {
        @include slide;
        background: white;
        box-shadow: 0 2px 19px 0 rgba(0, 0, 0, 0.21);
        font-size: initial;
        margin: 0 5%;
        width: 68%;

        &.active {
          transform: scale(1.12);
        }

        img {
          max-width: rem-calc(224);
          width: 100%;
        }

        > div:nth-of-type(1) {
          font-size: rem-calc(12);
          font-weight: $bold;
          letter-spacing: 0.3px;
          line-height: 1.57;
        }

        > div:nth-of-type(2) {
          font-size: rem-calc(12);
          letter-spacing: 0.3px;
          line-height: 1.5;
          margin-top: rem-calc(8);
        }
      }
    }
  }
</style>
